import { useEffect, useRef, useState } from 'react';
import { Drawer, Spin, Empty, Descriptions, Divider, Tabs, Table, Tag, Typography, Row, Col, Card, App } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { TeamOutlined, AppleOutlined, AndroidOutlined } from '@ant-design/icons';
import { BroadcastDetailResponse, BroadcastLanguageStats } from '@mygate/common';
import { broadcastsService } from '@/services/broadcasts';
import { ApiError } from '@/services/api';
import { formatDateTime } from '@/utils/date';
import { FilterSummary } from './FilterBuilder';
import { LangCode, LANG_KEYS, LANGUAGE_LABELS, BROADCAST_STATUS_COLORS } from './constants';

const { Title, Text } = Typography;

interface BroadcastDetailDrawerProps {
  broadcastId: string | null;
  open: boolean;
  onClose: () => void;
}

export function BroadcastDetailDrawer({ broadcastId, open, onClose }: BroadcastDetailDrawerProps) {
  const { message } = App.useApp();
  const [detail, setDetail] = useState<BroadcastDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const broadcastIdRef = useRef(broadcastId);

  useEffect(() => {
    broadcastIdRef.current = broadcastId;
    if (open && broadcastId) {
      setLoading(true);
      broadcastsService
        .getById(broadcastId)
        .then(response => {
          if (broadcastIdRef.current !== broadcastId) return;
          if (response.success && response.data) setDetail(response.data);
        })
        .catch((error: unknown) => {
          if (broadcastIdRef.current !== broadcastId) return;
          message.error(error instanceof ApiError ? error.message : 'Failed to load broadcast details');
        })
        .finally(() => {
          if (broadcastIdRef.current === broadcastId) setLoading(false);
        });
    }
    if (!open) {
      setDetail(null);
    }
  }, [open, broadcastId]);

  const perLanguageColumns: ColumnsType<BroadcastLanguageStats> = [
    {
      title: 'Language',
      dataIndex: 'language',
      key: 'language',
      render: (lang: LangCode) => LANGUAGE_LABELS[lang] || lang,
    },
    { title: 'Recipients', dataIndex: 'recipientCount', key: 'recipientCount' },
    {
      title: 'Sent',
      key: 'sent',
      render: (_, record) => (record.statsAvailable ? record.sent : '—'),
    },
    {
      title: 'Delivered',
      key: 'delivered',
      render: (_, record) => (record.statsAvailable ? record.delivered : '—'),
    },
    {
      title: 'Failed',
      key: 'failed',
      render: (_, record) => (record.statsAvailable ? record.failed : '—'),
    },
    {
      title: 'Error',
      dataIndex: 'error',
      key: 'error',
      render: (error?: string) => (error ? <Text type="danger">{error}</Text> : '-'),
    },
  ];

  return (
    <Drawer title="Broadcast details" open={open} onClose={onClose} width={640}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : detail ? (
        <>
          <Descriptions title="Overview" column={2} bordered size="small">
            <Descriptions.Item label="Status">
              <Tag color={BROADCAST_STATUS_COLORS[detail.broadcast.status] || 'default'}>{detail.broadcast.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Sent by">{detail.broadcast.sentBy}</Descriptions.Item>
            <Descriptions.Item label="Created">{formatDateTime(detail.broadcast.createdAt)}</Descriptions.Item>
            <Descriptions.Item label="Updated">{formatDateTime(detail.broadcast.updatedAt)}</Descriptions.Item>
            {detail.broadcast.errorMessage && (
              <Descriptions.Item label="Error" span={2}>
                <Text type="danger">{detail.broadcast.errorMessage}</Text>
              </Descriptions.Item>
            )}
          </Descriptions>

          <Divider />

          <Title level={5}>Content</Title>
          <Tabs
            items={LANG_KEYS.filter(lang => detail.broadcast.content[lang]).map(lang => {
              const c = detail.broadcast.content[lang];
              return {
                key: lang,
                label: LANGUAGE_LABELS[lang],
                children: (
                  <Descriptions column={1} bordered size="small">
                    <Descriptions.Item label="Title">{c?.title}</Descriptions.Item>
                    <Descriptions.Item label="Body">
                      <Text style={{ whiteSpace: 'pre-wrap' }}>{c?.body}</Text>
                    </Descriptions.Item>
                  </Descriptions>
                ),
              };
            })}
          />

          <Divider />

          <Title level={5}>Audience filter</Title>
          <FilterSummary filter={detail.broadcast.filter} />

          <Divider />

          <Title level={5}>Audience</Title>
          <Row gutter={16}>
            <Col span={8}>
              <Card size="small">
                <TeamOutlined /> <Text strong>{detail.broadcast.audience.total}</Text>{' '}
                <Text type="secondary">total</Text>
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small">
                <AppleOutlined /> <Text strong>{detail.broadcast.audience.byPlatform.ios}</Text>{' '}
                <Text type="secondary">iOS</Text>
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small">
                <AndroidOutlined /> <Text strong>{detail.broadcast.audience.byPlatform.android}</Text>{' '}
                <Text type="secondary">Android</Text>
              </Card>
            </Col>
          </Row>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">
              By language:{' '}
              {(Object.entries(detail.broadcast.audience.byLanguage) as Array<[LangCode, number]>)
                .filter(([, count]) => count > 0)
                .map(([lang, count]) => `${LANGUAGE_LABELS[lang]} ${count}`)
                .join(', ') || 'n/a'}
              {' · '}
              Excluded by opt-out: {detail.broadcast.audience.excludedByOptOut}
              {/* MYG-821: present on broadcasts sent since the consent rule exists */}
              {detail.broadcast.audience.consentRequired !== undefined && (
                <>
                  {' · '}
                  {detail.broadcast.audience.consentRequired
                    ? `Explicit consent required: ${detail.broadcast.audience.excludedWithoutConsent ?? 0} left out without it`
                    : `With explicit consent: ${detail.broadcast.audience.withExplicitConsent ?? 0} (not required)`}
                </>
              )}
            </Text>
          </div>

          <Divider />

          <Title level={5}>Delivery by language</Title>
          <Table
            dataSource={detail.perLanguage}
            columns={perLanguageColumns}
            rowKey="language"
            pagination={false}
            size="small"
          />

          <Divider />

          <Title level={5}>Overall stats</Title>
          <Descriptions column={3} bordered size="small">
            <Descriptions.Item label="Sent">{detail.stats.statsAvailable ? detail.stats.sent : '—'}</Descriptions.Item>
            <Descriptions.Item label="Delivered">
              {detail.stats.statsAvailable ? detail.stats.delivered : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Failed">
              {detail.stats.statsAvailable ? detail.stats.failed : '—'}
            </Descriptions.Item>
          </Descriptions>
          {!detail.stats.statsAvailable && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Delivery stats expire after 30 days.
            </Text>
          )}
        </>
      ) : (
        <Empty description="No broadcast selected" />
      )}
    </Drawer>
  );
}
